import React, {useEffect, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Flex,
    Heading,
    Stack,
    useBreakpointValue,
    useMultiStyleConfig
} from '@chakra-ui/react'
import DisplayPrice from '../display-price'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import DynamicImage from '@app/components/dynamic-image'
import {getPriceData} from '../../utils/product-utils'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {useShopperBasketsMutationHelper, useProducts} from '@salesforce/commerce-sdk-react'
import {useIntl, FormattedMessage} from 'react-intl'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useToast} from '@app/hooks/use-toast'
import {useGTM} from '@app/hooks/use-gtm'
import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'
import {useLocation} from 'react-router-dom'
import Link from '../link'
import {productUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'
import SwatchGroup from '../swatch-group'

const ShopLookDrawerButton = ({onClick, productId, onMoreDetailsClick, isDisabled}) => (
    <Stack textAlign={'center'} spacing={10}>
        <Button key="cart-button" onClick={onClick} disabled={isDisabled} variant="primary">
            <FormattedMessage defaultMessage="Add to Cart" id="product_view.button.add_to_cart" />
        </Button>
        <Link onClick={onMoreDetailsClick} to={productUrlBuilder({id: productId})}>
            <FormattedMessage defaultMessage="More details" id="product_view.link.more_details" />
        </Link>
    </Stack>
)

const ShopLookDrawerProducts = ({products, onClose, styles, activeCurrency}) => {
    const isMobile = useBreakpointValue({base: true, lg: false})
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const intl = useIntl()
    const [selectedVariants, setSelectedVariants] = useState({})
    const showToast = useToast()
    const location = useLocation()
    const {onOpen: onAddToCartModalOpen, onClose: onAddToCartModalClose, isOpen: isAddToCartModalOpen} = useAddToCartModalContext()
    const {triggerAddToCartEvent} = useGTM()

    useEffect(() => {
        if (isAddToCartModalOpen) onAddToCartModalClose()
    }, [location.pathname])

    const productIds = products?.map((product) => product.productId || product.id)
    const {data: productSearchResult} = useProducts(
        {
            parameters: {
                ids: productIds?.join(','),
                allImages: true,
                select: '(data.(id, variationAttributes.(**), imageGroups.(**)))'
            }
        },
        {enabled: !!productIds?.length}
    )

    const processedProducts = useMemo(() => {
        if (!products) return []
        return products.map((product) => {
            const apiData = productSearchResult?.data?.find((p) => p.id === (product.productId || product.id))
            const updatedProduct = {...product, variationAttributes: apiData?.variationAttributes || product.variationAttributes, imageGroups: apiData?.imageGroups || product.imageGroups}
            if (updatedProduct.variationAttributes) {
                const sizeAttr = updatedProduct.variationAttributes.find((attr) => attr.id === 'size')
                if (sizeAttr && updatedProduct.variants) {
                    const sizeOrder = sizeAttr.values.map((v) => v.value)
                    updatedProduct.variants.sort((a, b) => sizeOrder.indexOf(a.variationValues.size) - sizeOrder.indexOf(b.variationValues.size))
                }
            }
            return updatedProduct
        })
    }, [products, productSearchResult])

    const handleChangeVariant = (productId, variant) => {
        setSelectedVariants((prev) => ({...prev, [productId]: variant}))
    }

    const SizeSwatch = ({item, isSelected, onClick, isFocusable}) => {
        const swatchStyles = useMultiStyleConfig('SwatchGroup', {variant: 'size', disabled: !item.orderable, selected: isSelected})
        return (
            <Button
                type="button"
                role="radio"
                aria-checked={isSelected}
                tabIndex={isFocusable ? 0 : -1}
                onClick={onClick}
                {...swatchStyles.swatch}
            >
                {item.variationValues.size}
            </Button>
        )
    }

    return (
        <Stack spacing={20}>
            {processedProducts.map((product) => {
                const productId = product.id || product.productId
                const variants = product.variants || []
                const firstOrderable = variants.find(v => v.orderable)
                const firstOrderableIndex = variants.findIndex(v => v.orderable)
                const activeVariant = selectedVariants[productId] || firstOrderable
                const image = findImageGroupBy(product.imageGroups, {viewType: 'front', selectedVariationAttributes: activeVariant?.variationValues || product.variationValues})?.images?.[0] || product.image

                return (
                    <Stack spacing={10} key={`prod-stack-${productId}`}>
                        <Flex gap={20}>
                            <Link to={productUrlBuilder({ id: productId })}>
                                {image && <DynamicImage width={isMobile ? '125px' : '180px'} src={`${image?.disBaseLink || image?.link}[?sw={width}]`} widths={[85, 147, 170, 294]} imageProps={{ alt: image?.alt, loading: 'lazy' }} />}
                            </Link>
                            <Box width="100%">
                                <Heading as={'div'} {...styles.productName}>
                                    <Link to={productUrlBuilder({ id: productId })} textDecoration="none">{product.name || product.productName}</Link>
                                </Heading>
                                <DisplayPrice priceData={getPriceData(product)} currency={product?.currency || activeCurrency} {...styles.productPrice} />
                                <Box p="24px 0">
                                    <SwatchGroup
                                        variant="size"
                                        value={activeVariant?.productId}
                                        handleChange={(val) => {
                                            const v = variants.find(v => v.productId === val)
                                            if (v) handleChangeVariant(productId, v)
                                        }}
                                    >
                                        {variants.map((item) => {
                                            return (
                                                <SizeSwatch
                                                    key={`${productId}-${item.productId}`}
                                                    item={item}
                                                    value={item.productId}
                                                    isSelected={activeVariant?.productId === item.productId}
                                                    onClick={() => handleChangeVariant(productId, item)}
                                                />
                                            )
                                        })}
                                    </SwatchGroup>
                                </Box>
                                <HideOnMobile>
                                    <ShopLookDrawerButton onClick={() => handleCartItem(product, activeVariant)} productId={productId} isDisabled={!activeVariant?.orderable} />
                                </HideOnMobile>
                            </Box>
                        </Flex>
                        <HideOnDesktop>
                            <ShopLookDrawerButton onClick={() => handleCartItem(product, activeVariant)} productId={productId} isDisabled={!activeVariant?.orderable} />
                        </HideOnDesktop>
                    </Stack>
                )
            })}
        </Stack>
    )
}

export default ShopLookDrawerProducts;